import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Feather, ArrowLeft, User, BookOpen, Award, TrendingUp, Loader2, Check, Flame, Heart, Mail, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
import { formatApiErrorDetail } from '@/utils/api';
import { ShareBadge } from '@/components/ShareBadge';
import { PlanStatusCard } from '@/components/PlanStatusCard';
import { ReceiptsCard } from '@/components/ReceiptsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [waitlist, setWaitlist] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    fetchStats();
    if (user?.role === 'admin') {
      axios
        .get(`${API}/waitlist`, { withCredentials: true })
        .then(({ data }) => setWaitlist(data))
        .catch(() => setWaitlist([]));
      axios
        .get(`${API}/feedback`, { withCredentials: true })
        .then(({ data }) => setFeedback(data))
        .catch(() => setFeedback([]));
      axios
        .get(`${API}/metrics`, { withCredentials: true })
        .then(({ data }) => setMetrics(data))
        .catch(() => setMetrics(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/profile/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === user?.name) return;

    setSaving(true);
    setError('');
    setSaveSuccess(false);

    try {
      const { data } = await axios.put(
        `${API}/profile`,
        { name: name.trim() },
        { withCredentials: true }
      );
      setUser(data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const initials = (user?.name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="profile-page">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-sm border-b border-amber-100 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-amber-900 hover:bg-amber-100"
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex items-center space-x-2">
              <div className="bg-amber-100 p-2 rounded-full">
                <Feather className="h-5 w-5 text-amber-800" />
              </div>
              <span className="font-display text-lg font-bold text-amber-950">Flash Fiction</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-amber-950 mb-2" data-testid="profile-title">Your Profile</h1>
          <p className="text-amber-800/70">Manage your account and celebrate your writing journey.</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-testid="profile-error">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Profile card */}
          <div className="md:col-span-1 space-y-6">
          <Card className="border-amber-100 shadow-sm">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-24 w-24 rounded-full ring-4 ring-amber-100 object-cover"
                    data-testid="profile-picture"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center text-3xl font-bold text-white ring-4 ring-amber-100" data-testid="profile-initials">
                    {initials}
                  </div>
                )}
              </div>
              <CardTitle className="font-display text-2xl text-amber-950" data-testid="profile-name-display">{user?.name}</CardTitle>
              <CardDescription data-testid="profile-email-display">{user?.email}</CardDescription>
              {user?.auth_provider === 'google' && (
                <div className="inline-flex items-center space-x-1 mt-2 text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                  <span>Signed in with Google</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="display-name">Display Name</Label>
                  <Input
                    id="display-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="profile-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving || !name.trim() || name.trim() === user?.name}
                  className="w-full bg-amber-900 hover:bg-amber-800 text-amber-50"
                  data-testid="save-profile-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Saved!
                    </>
                  ) : (
                    'Update Profile'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          <PlanStatusCard />
          <ReceiptsCard planExpiresAt={user?.plan_expires_at} />
          </div>

          {/* Stats */}
          <div className="md:col-span-2 space-y-6">
            {loadingStats ? (
              <Card className="border-amber-100 shadow-sm">
                <CardContent className="py-12 flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-800" />
                </CardContent>
              </Card>
            ) : stats ? (
              <>
                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="border-amber-100 shadow-sm" data-testid="stat-total-stories">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <BookOpen className="h-5 w-5 text-amber-700" />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-total-stories-value">{stats.total_stories}</div>
                      <div className="text-sm text-amber-800/60">Stories written</div>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-100 shadow-sm" data-testid="stat-total-words">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <TrendingUp className="h-5 w-5 text-orange-700" />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-total-words-value">{stats.total_words.toLocaleString()}</div>
                      <div className="text-sm text-amber-800/60">Total words</div>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-100 shadow-sm" data-testid="stat-avg-words">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <User className="h-5 w-5 text-rose-700" />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-avg-words-value">{stats.average_words}</div>
                      <div className="text-sm text-amber-800/60">Average / story</div>
                    </CardContent>
                  </Card>
                  <Card className={`border-amber-100 shadow-sm ${(stats.current_streak || 0) > 0 ? 'bg-gradient-to-br from-orange-50 to-rose-50' : ''}`} data-testid="stat-streak">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Flame className={`h-5 w-5 ${(stats.current_streak || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-streak-value">
                        {stats.current_streak || 0}
                        <span className="text-sm font-normal text-amber-800/60 ml-1">
                          {(stats.current_streak || 0) === 1 ? 'day' : 'days'}
                        </span>
                      </div>
                      <div className="text-sm text-amber-800/60">
                        Current streak
                        {(stats.longest_streak || 0) > (stats.current_streak || 0) && (
                          <span className="block text-xs mt-0.5">Best: {stats.longest_streak} days</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-100 shadow-sm" data-testid="stat-reactions">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Heart className={`h-5 w-5 ${(stats.total_reactions || 0) > 0 ? 'fill-rose-500 text-rose-500' : 'text-gray-400'}`} />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-reactions-value">{stats.total_reactions || 0}</div>
                      <div className="text-sm text-amber-800/60">Reader hearts</div>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-100 shadow-sm" data-testid="stat-milestones-count">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Award className="h-5 w-5 text-amber-700" />
                      </div>
                      <div className="font-display text-3xl font-bold text-amber-950" data-testid="stat-milestones-value">{stats.milestones.length}</div>
                      <div className="text-sm text-amber-800/60">Milestones earned</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Favorite category */}
                {stats.favorite_category && (
                  <Card className="border-amber-100 shadow-sm bg-gradient-to-br from-amber-100/50 to-orange-100/50" data-testid="favorite-category-card">
                    <CardContent className="p-5">
                      <div className="text-sm text-amber-800/60 mb-1">Your favorite genre</div>
                      <div className="font-display text-2xl font-bold text-amber-950 capitalize" data-testid="favorite-category-value">
                        {stats.favorite_category}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Shareable badge */}
                {stats.total_stories > 0 && (
                  <Card className="border-amber-100 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-display text-xl text-amber-950">Your writer badge</CardTitle>
                      <CardDescription>Download and share it to invite fellow writers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ShareBadge user={user} stats={stats} />
                    </CardContent>
                  </Card>
                )}

                {/* Milestones */}
                <Card className="border-amber-100 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-display text-xl flex items-center text-amber-950">
                      <Award className="h-5 w-5 mr-2 text-amber-700" />
                      Milestones
                    </CardTitle>
                    <CardDescription>Achievements as you write</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats.milestones.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="milestones-list">
                        {stats.milestones.map((m, i) => (
                          <div
                            key={m.name}
                            className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
                            data-testid={`milestone-${i}`}
                          >
                            <div className="bg-amber-200 p-2 rounded-full">
                              <Check className="h-4 w-4 text-amber-800" />
                            </div>
                            <span className="text-sm font-medium text-amber-900">{m.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-800/60 py-4 text-center" data-testid="milestones-empty">
                        {stats.total_stories > 0
                          ? 'Keep writing to unlock your first milestone!'
                          : 'Write your first story to unlock milestones!'}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Category breakdown */}
                {Object.keys(stats.category_distribution).length > 0 && (
                  <Card className="border-amber-100 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-display text-xl text-amber-950">Genre breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3" data-testid="category-breakdown">
                        {Object.entries(stats.category_distribution).map(([cat, count]) => {
                          const percentage = (count / stats.total_stories) * 100;
                          return (
                            <div key={cat} data-testid={`category-bar-${cat}`}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="capitalize font-medium text-amber-900">{cat}</span>
                                <span className="text-amber-800/60">{count} {count === 1 ? 'story' : 'stories'}</span>
                              </div>
                              <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admin: validation metrics */}
                {user?.role === 'admin' && metrics && (
                  <Card className="border-amber-100 shadow-sm" data-testid="metrics-admin-card">
                    <CardHeader>
                      <CardTitle className="font-display text-xl text-amber-950 flex items-center">
                        <TrendingUp className="h-5 w-5 mr-2 text-amber-700" />
                        Validation metrics
                      </CardTitle>
                      <CardDescription>Everything you need to judge demand at a glance</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="metrics-grid">
                        {[
                          ['Writers', metrics.writers, 'writers'],
                          ['Stories', metrics.stories, 'stories'],
                          ['Shared', metrics.shared_stories, 'shared'],
                          ['Story views', metrics.story_views, 'views'],
                          ['Hearts', metrics.hearts, 'hearts'],
                          ['Waitlist', metrics.waitlist_signups, 'waitlist'],
                          ['Feedback +', metrics.feedback_positive, 'fb-up'],
                          ['Feedback −', metrics.feedback_negative, 'fb-down'],
                          ['Paying writers', metrics.paying_writers, 'paying'],
                          ['Payments', metrics.payments, 'payments'],
                          ['Revenue', `$${Number(metrics.revenue_usd).toFixed(2)}`, 'revenue'],
                        ].map(([label, value, key]) => (
                          <div key={key} className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center" data-testid={`metric-${key}`}>
                            <div className="font-display text-2xl font-bold text-amber-950">{value}</div>
                            <div className="text-xs text-amber-800/60 uppercase tracking-wider">{label}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admin: waitlist emails */}
                {user?.role === 'admin' && waitlist !== null && (
                  <Card className="border-amber-100 shadow-sm" data-testid="waitlist-admin-card">
                    <CardHeader>
                      <CardTitle className="font-display text-xl text-amber-950 flex items-center">
                        <Mail className="h-5 w-5 mr-2 text-amber-700" />
                        Waitlist signups ({waitlist.length})
                      </CardTitle>
                      <CardDescription>Emails collected from the landing page</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {waitlist.length === 0 ? (
                        <p className="text-sm text-amber-800/60" data-testid="waitlist-admin-empty">No signups yet — share your landing page to start collecting.</p>
                      ) : (
                        <ul className="space-y-2" data-testid="waitlist-admin-list">
                          {waitlist.map((entry) => (
                            <li key={entry.email} className="flex justify-between text-sm bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
                              <span className="text-amber-900 font-medium">{entry.email}</span>
                              <span className="text-amber-800/60">{new Date(entry.created_at).toLocaleDateString()}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Admin: tester feedback */}
                {user?.role === 'admin' && feedback !== null && (
                  <Card className="border-amber-100 shadow-sm" data-testid="feedback-admin-card">
                    <CardHeader>
                      <CardTitle className="font-display text-xl text-amber-950 flex items-center">
                        <MessageSquare className="h-5 w-5 mr-2 text-amber-700" />
                        Tester feedback ({feedback.length})
                      </CardTitle>
                      <CardDescription>
                        {feedback.length > 0 && (
                          <span data-testid="feedback-admin-summary">
                            {feedback.filter((f) => f.rating === 'up').length} positive &middot;{' '}
                            {feedback.filter((f) => f.rating === 'down').length} negative
                          </span>
                        )}
                        {feedback.length === 0 && 'Collected after users save a story'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {feedback.length === 0 ? (
                        <p className="text-sm text-amber-800/60" data-testid="feedback-admin-empty">No feedback yet — it appears here once testers save stories.</p>
                      ) : (
                        <ul className="space-y-2" data-testid="feedback-admin-list">
                          {feedback.map((f, i) => (
                            <li key={f.id} className="flex items-start gap-3 text-sm bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
                              {f.rating === 'up' ? (
                                <ThumbsUp className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                              ) : (
                                <ThumbsDown className="h-4 w-4 mt-0.5 shrink-0 text-rose-600" />
                              )}
                              <div className="flex-1">
                                <span className="text-amber-900 font-medium">{f.user_name}</span>
                                {f.comment && <p className="text-amber-800/80 mt-0.5">&ldquo;{f.comment}&rdquo;</p>}
                              </div>
                              <span className="text-amber-800/60 shrink-0">{new Date(f.created_at).toLocaleDateString()}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
